import { type FC, useEffect, useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Col, Container, Row } from '@crehana/ui';
import { useRouter } from 'next/router';

import type { BaseRouteType } from '@/shared/components/DashboardTemplate/types';

import { Breadcrumb, BreadcrumbsWrapper } from './breadcrumbs.styles';

interface BreadcrumbsProps {
  className?: string;
  extended?: boolean;
  isDark?: boolean;
  level: 1 | 2 | 3;
  position?: 'static' | 'sticky';
  section: BaseRouteType;
  transparent?: boolean;
}

interface BreadcrumbChild {
  changePage: (value: string, navigate: string) => void;
  isDark?: boolean;
  route: BaseRouteType;
}

const BreadcrumbChild: FC<React.PropsWithChildren<BreadcrumbChild>> = ({
  route,
  isDark,
  changePage,
}) => {
  const { pathname } = useRouter();
  const { t } = useTranslation();

  if (pathname.includes(route.path)) {
    return (
      <div className="flex">
        <Breadcrumb
          isDark={isDark}
          className="tw-font-body4"
          onClick={() => changePage(route.path, t(route.label))}
        >
          {'/ '} {t(route.label)}
        </Breadcrumb>
      </div>
    );
  }

  return <></>;
};

const Breadcrumbs: FC<React.PropsWithChildren<BreadcrumbsProps>> = ({
  className,
  extended,
  isDark,
  level,
  position = 'static',
  section,
  transparent,
}) => {
  const { pathname, query, push } = useRouter();
  const { slug, originalId } = query as {
    slug: string;
    originalId: string;
  };
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const [routesLevel3, setRoutesLevel3] = useState<BaseRouteType>();

  const changePage = (value: string, navigate: string) => {
    logEvent({
      event: 'Breadcrumb Navigation',
      data: {
        ...getSourceEventParams(),
        Organization: slug,
        'Section Menu': t(section.label),
        'Breadcrumb Navigate': navigate,
        Url: value.replace('[slug]', slug),
      },
    });

    const url = value
      .replace('[slug]', slug)
      .replace('[originalId]', originalId);

    push(url);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (level === 3) {
      section.nested?.map(r => {
        // biome-ignore lint/style/useBlockStatements: single-line early return
        if (pathname.includes(r.path)) setRoutesLevel3(r);
      });
    }
  }, [level]);

  return (
    <div
      className={`w-full ${position} ${
        transparent ? '' : isDark ? 'bg-base-dark' : 'bg-white'
      } ${className}
       top-0 right-0 left-0 z-5`}
    >
      <Container type="dashboard" full={extended}>
        <Row>
          <Col xs={12} sm={12} md={12} lg={12}>
            {section && (
              <BreadcrumbsWrapper>
                <Breadcrumb
                  isDark={isDark}
                  className="tw-font-body4"
                  onClick={() =>
                    section.nested
                      ? changePage(section.path, t(section.label))
                      : null
                  }
                >
                  {t(section.label)}
                </Breadcrumb>
                {level > 1 &&
                  section.nested?.map(route => {
                    return (
                      <BreadcrumbChild
                        key={route.key}
                        route={route}
                        isDark={isDark}
                        changePage={changePage}
                      />
                    );
                  })}
                {routesLevel3 &&
                  level === 3 &&
                  routesLevel3.nested?.map(route => {
                    return (
                      <BreadcrumbChild
                        key={route.key}
                        route={route}
                        isDark={isDark}
                        changePage={changePage}
                      />
                    );
                  })}
              </BreadcrumbsWrapper>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Breadcrumbs;
